import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

type LiveFilter = {
  FilterID: string;
  Name: string;
  FileKey: string;
  FileUrl: string;
  ThumbnailUrl?: string | null;
  UploadedBy: string;
  IsActive: boolean;
  UsageCount: number;
  createdAt?: string;
  updatedAt?: string;
};

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

const LiveFilters: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<LiveFilter[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const fetchFilters = useCallback(
    async (pageNum: number) => {
      const headers = headerWithAuth();
      if (!headers) return;

      setLoading(true);
      try {
        const res = await fetch(
          `${liveApiBase}/filters?page=${pageNum}&limit=${limit}&activeOnly=false`,
          { headers }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load filters');
        const data = json.data as { filters: LiveFilter[]; total: number; hasMore: boolean };
        setFilters(data.filters);
        setTotal(data.total);
        setHasMore(data.hasMore);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to fetch filters');
      } finally {
        setLoading(false);
      }
    },
    [headerWithAuth]
  );

  useEffect(() => {
    fetchFilters(page);
  }, [fetchFilters, page]);

  const handleDelete = useCallback(
    async (filterId: string) => {
      const headers = headerWithAuth();
      if (!headers) return;

      setDeletingId(filterId);
      try {
        const res = await fetch(`${liveApiBase}/filters/${encodeURIComponent(filterId)}`, {
          method: 'DELETE',
          headers,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to delete filter');
        toast.success(json.message || 'Filter deleted');
        setFilters((prev) => prev.filter((f) => f.FilterID !== filterId));
        setTotal((prev) => prev - 1);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to delete filter');
      } finally {
        setDeletingId('');
      }
    },
    [headerWithAuth]
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Live Filters</h1>
            <p className='text-sm text-gray-400'>DeepAR filters available for live streams.</p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='text-sm text-gray-400'>
              {loading ? 'Loading...' : `${total} filter${total !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => navigate('/live-filters/new')}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
            >
              Upload Filter
            </button>
          </div>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Fetching filters...</div>
        ) : filters.length === 0 ? (
          <div className='text-sm text-gray-400'>No filters found.</div>
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
              {filters.map((filter) => (
                <div
                  key={filter.FilterID}
                  className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg'
                >
                  <div className='h-48 bg-white/10 flex items-center justify-center overflow-hidden'>
                    {filter.ThumbnailUrl ? (
                      <img
                        src={filter.ThumbnailUrl}
                        alt={filter.Name}
                        className='w-full h-full object-cover'
                      />
                    ) : (
                      <div className='text-sm text-gray-300 flex flex-col items-center gap-2'>
                        <svg className='w-12 h-12 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <span className='text-xs text-gray-500'>.deepar filter</span>
                      </div>
                    )}
                  </div>
                  <div className='p-4 space-y-2'>
                    <div className='text-sm font-semibold text-white'>{filter.Name}</div>
                    <div className='text-xs text-gray-400 truncate'>ID: {filter.FilterID}</div>
                    <div className='flex items-center gap-2 text-xs'>
                      <span className='text-gray-400'>Used: {filter.UsageCount}×</span>
                      <span
                        className={`px-2 py-0.5 rounded-full border ${
                          filter.IsActive
                            ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                            : 'text-red-300 border-red-400/40 bg-red-500/10'
                        }`}
                      >
                        {filter.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <a
                      href={filter.FileUrl}
                      target='_blank'
                      rel='noreferrer'
                      className='text-xs text-[#FF9C15] hover:underline break-all block'
                    >
                      {filter.FileKey}
                    </a>
                    {filter.createdAt && (
                      <div className='text-xs text-gray-500'>
                        {new Date(filter.createdAt).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={() => handleDelete(filter.FilterID)}
                      disabled={deletingId === filter.FilterID}
                      className='mt-2 w-full px-3 py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                    >
                      {deletingId === filter.FilterID ? 'Deleting...' : 'Delete Filter'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className='flex items-center justify-between mt-8'>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Previous
              </button>
              <span className='text-xs text-gray-400'>Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveFilters;
